import {
  OkResponse,
  PriceCheckResult,
  type Product,
  ProductPricing,
  formatBRL,
  formatBps,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { AffixInput, Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, formatGrams, formatHm, parseCentavos } from '../../lib/numbers';
import { productsKey, useInvalidateProducts } from '../../lib/products-api';
import { useDebounced } from '../../lib/use-debounced';

type Row = ProductPricing['rows'][number];
type Cell = Row['cells'][number];

const pricingKey = (id: string) => [...productsKey, id, 'pricing'];

export function useProductPricing(productId: string) {
  return useQuery({
    queryKey: pricingKey(productId),
    queryFn: () => api.get(`/products/${productId}/pricing`, ProductPricing),
  });
}

function useSetPrice(productId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: (v: { variantId: string; channelId: string; price: number | null }) =>
      api.put(`/variants/${v.variantId}/prices/${v.channelId}`, OkResponse, {
        listedPriceCentavos: v.price,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pricingKey(productId) });
      invalidate();
    },
  });
}

const toneOf = (state: string | undefined, minOk: boolean) =>
  state === 'below_cost'
    ? 'var(--err)'
    : !minOk || state === 'below_minimum_margin'
      ? 'var(--warn)'
      : 'var(--ink)';

function line(label: string, value: number | undefined) {
  return (
    <div className="kv sm">
      <span className="muted">{label}</span>
      <span className="v">
        {value === undefined ? '—' : value === 0 ? formatBRL(0) : `− ${formatBRL(value)}`}
      </span>
    </div>
  );
}

function PriceDialog({
  productId,
  row,
  cell,
  channel,
  minimum,
  onClose,
}: {
  productId: string;
  row: Row;
  cell: Cell;
  channel: string;
  minimum: number | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const set = useSetPrice(productId);
  const suggested = cell.result?.suggested?.priceCentavos ?? null;
  const [text, setText] = useState(centavosInput(cell.listedPriceCentavos ?? suggested ?? 0));
  const price = parseCentavos(text);
  const debounced = useDebounced(price, 300);
  const check = useQuery({
    queryKey: ['price-check', row.variant.id, cell.channelId, debounced],
    queryFn: () =>
      api.post(`/variants/${row.variant.id}/price-check`, PriceCheckResult, {
        channelId: cell.channelId,
        priceCentavos: debounced,
      }),
    enabled: debounced !== null && debounced > 0,
    placeholderData: (prev) => prev,
  });
  const ev = check.data?.result.evaluation;
  const fees = ev?.fees;
  const cliff = check.data?.result.warnings.find((w) => w.code === 'fee_cliff');
  const note = !ev
    ? null
    : ev.netProfitCentavos < 0
      ? `Abaixo do custo: prejuízo de ${formatBRL(-ev.netProfitCentavos)}`
      : ev.state === 'below_minimum_margin'
        ? `Abaixo da margem mínima${minimum !== null ? ` (${formatBps(minimum)})` : ''}`
        : cliff && cliff.code === 'fee_cliff'
          ? `A ${formatBRL(cliff.suggestedPriceCentavos)} o lucro é ${formatBRL(cliff.netProfitCentavos)} (${formatBps(cliff.marginBps)}).`
          : fees?.sellerPaysShipping
            ? `A esse preço o vendedor paga o frete (média ${formatBRL(fees.shipping)}).`
            : null;
  const save = () => {
    if (price === null || price <= 0) {
      toast('Digite um preço válido');
      return;
    }
    set.mutate(
      { variantId: row.variant.id, channelId: cell.channelId, price },
      {
        onSuccess: () => {
          toast(
            `Preço salvo: ${formatBRL(price)}${ev ? ` · margem ${formatBps(ev.marginBps)}` : ''}`,
          );
          onClose();
        },
        onError: (err) => toast(errorMessage(err)),
      },
    );
  };
  return (
    <Modal
      title={`Preço de ${row.variant.name} · ${channel}`}
      onClose={onClose}
      footer={
        <>
          {cell.listedPriceCentavos !== null ? (
            <button
              type="button"
              className="btn g"
              onClick={() =>
                set.mutate(
                  { variantId: row.variant.id, channelId: cell.channelId, price: null },
                  {
                    onSuccess: () => {
                      toast('Preço removido');
                      onClose();
                    },
                  },
                )
              }
            >
              Remover preço
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn p" disabled={set.isPending} onClick={save}>
            Salvar preço
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="pr-new" label="Novo preço">
          <AffixInput id="pr-new" prefix="R$" value={text} onChange={setText} />
        </Field>
        <Field
          label={`Sugerido${cell.result ? ` (${formatBps(cell.result.targetMarginBps)})` : ''}`}
        >
          <div className="input readonly">
            <span className="tab">{suggested !== null ? formatBRL(suggested) : '—'}</span>
          </div>
        </Field>
      </div>
      <div className="card sunk col" style={{ gap: 0, padding: '6px 14px' }}>
        {line('Custo unitário', check.data?.unitCostCentavos)}
        {line('Comissão e taxa', fees ? fees.commission + fees.paymentFee : undefined)}
        {line('Tarifa fixa', fees?.fixedFee)}
        {line('Frete grátis', fees?.shipping)}
        {fees?.tax ? line('Imposto', fees.tax) : null}
        <div className="kv">
          <span style={{ fontWeight: 600 }}>Lucro</span>
          <span
            className="v"
            style={{
              fontWeight: 600,
              color: ev ? (ev.state === 'ok' ? 'var(--ok)' : toneOf(ev.state, true)) : undefined,
            }}
          >
            {ev ? `${formatBRL(ev.netProfitCentavos)} · ${formatBps(ev.marginBps)}` : '—'}
          </span>
        </div>
      </div>
      {note ? (
        <p
          className="sm"
          style={{ color: ev?.state === 'ok' ? 'var(--ink2)' : toneOf(ev?.state, false) }}
        >
          {note}
        </p>
      ) : null}
      {check.error ? <p className="help err">{errorMessage(check.error)}</p> : null}
    </Modal>
  );
}

/** "Preços" tab (board Produto-Visao, Preços). */
export function PricesTab({ product }: { product: Product }) {
  const pricing = useProductPricing(product.id);
  const toast = useToast();
  const set = useSetPrice(product.id);
  const [editing, setEditing] = useState<{ row: Row; cell: Cell } | null>(null);
  const p = pricing.data;

  if (product.variants.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <div className="emptyart" />
          <h2>Sem preços ainda</h2>
          <p>Os preços por canal aparecem quando a primeira variação tiver perfil de impressão.</p>
          <Link className="btn p" to={`/produtos/${product.id}/variacoes`}>
            Ir para Variações
          </Link>
        </div>
      </div>
    );
  }
  if (!p) return <div className="skel" style={{ height: 240 }} />;

  const minimum = p.minimumMarginBps;
  const channelName = (id: string) => p.channels.find((c) => c.id === id)?.name ?? 'canal';
  // The first listed price with a better price just below a fee threshold (board margin alert).
  const cliff = p.rows
    .flatMap((row) => row.cells.map((cell) => ({ row, cell })))
    .find(
      ({ cell }) =>
        cell.listedPriceCentavos !== null &&
        cell.result?.warnings.some((w) => w.code === 'fee_cliff'),
    );
  const cliffWarning = cliff?.cell.result?.warnings.find((w) => w.code === 'fee_cliff');
  const cols = `1.5fr .9fr ${p.channels.map(() => '1fr').join(' ')}`;

  return (
    <div className="col" style={{ gap: 16 }}>
      {cliff &&
      cliffWarning &&
      cliffWarning.code === 'fee_cliff' &&
      cliff.cell.result?.evaluation ? (
        <div className="alert warn">
          <Icon name="alert" />
          <div className="col grow" style={{ gap: 6 }}>
            <p>
              <b>
                {product.name} {cliff.row.variant.name} no {channelName(cliff.cell.channelId)} está
                com {formatBps(cliff.cell.result.evaluation.marginBps)} de margem.
              </b>{' '}
              A {formatBRL(cliff.cell.listedPriceCentavos ?? 0)} o anúncio passa do limite de{' '}
              {formatBRL(cliffWarning.thresholdCentavos)}
              {cliffWarning.freeShipping ? ' e entra o frete grátis' : ' e a tarifa muda'}.
            </p>
            <p className="sm">
              A {formatBRL(cliffWarning.suggestedPriceCentavos)}: lucro de{' '}
              {formatBRL(cliffWarning.netProfitCentavos)} ({formatBps(cliffWarning.marginBps)}).
            </p>
            <button
              type="button"
              className="btn sm p"
              style={{ alignSelf: 'flex-start' }}
              onClick={() =>
                set.mutate(
                  {
                    variantId: cliff.row.variant.id,
                    channelId: cliff.cell.channelId,
                    price: cliffWarning.suggestedPriceCentavos,
                  },
                  {
                    onSuccess: () =>
                      toast(
                        `${cliff.row.variant.name} no ${channelName(cliff.cell.channelId)}: ${formatBRL(cliffWarning.suggestedPriceCentavos)} · margem ${formatBps(cliffWarning.marginBps)}`,
                      ),
                  },
                )
              }
            >
              Usar {formatBRL(cliffWarning.suggestedPriceCentavos)}
            </button>
          </div>
        </div>
      ) : null}

      <section className="card flat" style={{ overflowX: 'auto' }}>
        <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 10 }}>
          <h2>Preço anunciado vs. sugerido</h2>
          <span className="xs muted">custos de hoje · toque num preço para mudar</span>
        </div>
        {p.channels.length === 0 ? (
          <p className="sm muted" style={{ padding: '8px 18px 16px' }}>
            Cadastre um canal com taxas em vigor em <Link to="/configuracoes/canais">Canais</Link>.
          </p>
        ) : (
          <div className="gt" style={{ minWidth: 220 + p.channels.length * 130 }}>
            <div className="gtr head" style={{ gridTemplateColumns: cols }}>
              <span>Variação</span>
              <span className="num">Custo un.</span>
              {p.channels.map((c) => (
                <span key={c.id} className="num row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <span className="dot" style={{ background: c.color }} />
                  {c.name}
                </span>
              ))}
            </div>
            {p.rows.map((row) => {
              const low = row.cells.some(
                (c) =>
                  c.listedPriceCentavos !== null &&
                  c.result?.evaluation &&
                  c.result.evaluation.state !== 'ok',
              );
              return (
                <div
                  key={row.variant.id}
                  className={low ? 'gtr wl' : 'gtr'}
                  style={{ gridTemplateColumns: cols }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 500, display: 'block' }}>{row.variant.name}</span>
                    <span className="xs muted">
                      {row.variant.sku}
                      {row.perUnit
                        ? ` · ${formatGrams(row.perUnit.grams)} · ${formatHm(row.perUnit.printSeconds)}`
                        : ''}
                    </span>
                  </span>
                  <span className="num">
                    {row.unitCostCentavos !== null ? (
                      formatBRL(row.unitCostCentavos)
                    ) : (
                      <span className="xs muted">{row.missing ?? 'perfil pendente'}</span>
                    )}
                  </span>
                  {row.cells.map((cell) => {
                    const ev = cell.result?.evaluation;
                    const sug = cell.result?.suggested?.priceCentavos ?? null;
                    if (!cell.result) {
                      return (
                        <span key={cell.channelId} className="num xs muted">
                          —
                        </span>
                      );
                    }
                    const listed = cell.listedPriceCentavos;
                    const belowMin = minimum !== null && ev ? ev.marginBps < minimum : false;
                    return (
                      <button
                        key={cell.channelId}
                        type="button"
                        className="pcell"
                        aria-label={`Mudar preço de ${row.variant.name} no ${channelName(cell.channelId)}`}
                        onClick={() => setEditing({ row, cell })}
                      >
                        <b
                          style={{
                            fontWeight: 600,
                            display: 'block',
                            color: listed !== null ? toneOf(ev?.state, !belowMin) : 'var(--ink2)',
                          }}
                        >
                          {listed !== null
                            ? formatBRL(listed)
                            : sug !== null
                              ? `sug. ${formatBRL(sug)}`
                              : 'sem preço'}
                        </b>
                        <span
                          className="xs"
                          style={{
                            color: listed !== null && belowMin ? 'var(--warn)' : 'var(--ink2)',
                            fontWeight: belowMin ? 600 : 400,
                          }}
                        >
                          {listed === null
                            ? 'não anunciado'
                            : ev
                              ? `${sug !== null && sug !== listed ? `sug. ${formatBRL(sug)} · ` : ''}${formatBps(ev.marginBps)}${belowMin ? ` · abaixo de ${formatBps(minimum ?? 0)}` : ''}`
                              : 'margem impossível'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
        <p className="xs muted" style={{ padding: '10px 18px 16px' }}>
          {p.targetMarginBps !== null ? `Margem alvo ${formatBps(p.targetMarginBps)} · ` : ''}
          {minimum !== null ? `mínima ${formatBps(minimum)} · ` : ''}“sug.” aparece quando o preço
          anunciado difere do sugerido. Confira as tarifas em{' '}
          <Link to="/configuracoes/canais">Canais</Link>.
        </p>
      </section>
      {editing ? (
        <PriceDialog
          productId={product.id}
          row={editing.row}
          cell={editing.cell}
          channel={channelName(editing.cell.channelId)}
          minimum={minimum}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
